import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, ActionCreator, Creator, Store } from '@ngrx/store';
import { concatLatestFrom } from '@ngrx/operators';
import { EMPTY, of } from 'rxjs';
import { catchError, exhaustMap, map, retry, tap } from 'rxjs/operators';
import {
  ActiveTenantStore,
  AppConfigSettings,
  AuthUserStore,
  CoreService,
  LoginSuccess,
  PermissionV1Service,
  User,
} from '@arc/arc-shared-frontend';
import {
  PaymentLinkService,
  PushNotificationsService,
} from '@arc/unified-payments-frontend/shared-payment';
import { selectCurrentLanguage } from '@arc/unified-payments-frontend/i18n';
import { selectSessionId } from '../session-store';
import { selectTenantId } from '../external-selectors';
import {
  selectSignInEmailToken,
  selectSignInUsername,
} from '../btpay/selectors/btpay.selectors';
import { selectVerifyUserIsProvisionedRetryConfig } from '../shared-selectors';

/**
 * Factory that creates a reusable NgRx functional effect for the sign-in email
 * verification step of the Payment Links flow.
 *
 * The effect listens for a configurable trigger action that carries a username,
 * calls POST /v1/tenants/{tenantId}/user/verify with the username and current
 * session ID, then dispatches either the provided success or error action.
 *
 * Usage (POI, POA, international — each passes its own action set):
 *
 *   export const poiSignInVerifyUserEffect = createVerifyUserEffect({
 *     triggerAction: PoiMakePaymentComponentActions.signInBannerSignedIn,
 *     successAction: MakePaymentPaymentLinksApiActions.signInEmailSent,
 *     errorAction:   MakePaymentPaymentLinksApiActions.signInEmailError,
 *   });
 */
export function createVerifyUserEffect<
  A extends Action<string> & { username: string },
>(config: {
  triggerAction: ActionCreator<string, Creator<any[], A>>;
  successAction: ActionCreator;
  errorAction: ActionCreator;
}) {
  return createEffect(
    (
      actions$ = inject(Actions),
      store = inject(Store),
      paymentLinkService = inject(PaymentLinkService),
    ) =>
      actions$.pipe(
        ofType(config.triggerAction),
        concatLatestFrom(() => [
          store.select(selectTenantId),
          store.select(selectSessionId),
        ]),
        exhaustMap(([action, tenantId, sessionId]) =>
          paymentLinkService
            .verifyUser(tenantId, { username: action.username, sessionId })
            .pipe(
              map((response) => config.successAction(response) as Action),
              catchError((error: HttpErrorResponse) =>
                of(config.errorAction({ error }) as Action),
              ),
            ),
        ),
      ),
    { functional: true },
  );
}

/**
 * Factory that creates a reusable non-dispatching NgRx functional effect that
 * subscribes to email verification push notifications via PushNotificationsService.
 *
 * The effect listens for a configurable trigger action (typically signInEmailSent),
 * then opens a subscription to the emailverification channel. The success and error
 * callbacks dispatch the provided actions when a notification arrives.
 *
 * Usage (POI, POA, international — each passes its own action set):
 *
 *   export const poiSubscribeToEmailVerificationEffect =
 *     createSubscribeToEmailVerificationEffect({
 *       triggerAction: MakePaymentPaymentLinksApiActions.signInEmailSent,
 *       successAction: MakePaymentPaymentLinksApiActions.emailVerificationReceived,
 *       errorAction:   MakePaymentPaymentLinksApiActions.emailVerificationError,
 *     });
 */
export function createSubscribeToEmailVerificationEffect<
  A extends Action<string>,
>(config: {
  triggerAction: ActionCreator<string, Creator<any[], A>>;
  successAction: ActionCreator;
  errorAction: ActionCreator;
}) {
  return createEffect(
    (
      actions$ = inject(Actions),
      store = inject(Store),
      pushNotificationsService = inject(PushNotificationsService),
    ) =>
      actions$.pipe(
        ofType(config.triggerAction),
        exhaustMap(() => {
          pushNotificationsService.subscribeToEmailVerificationEvents(
            (notification) =>
              store.dispatch(config.successAction({ notification }) as Action),
            (error) => store.dispatch(config.errorAction({ error }) as Action),
          );
          return EMPTY;
        }),
      ),
    { functional: true, dispatch: false },
  );
}

/**
 * Factory that creates a reusable NgRx functional effect that issues a platform
 * JWT after email verification completes.
 *
 * The effect listens for a configurable trigger action (typically
 * emailVerificationReceived), calls
 * POST /v1/tenants/{tenantId}/auth/issuePlatformJwt with the current session ID
 * and locale, then dispatches either the provided success or error action.
 *
 * Usage (POI, POA, international — each passes its own action set):
 *
 *   export const poiIssuePlatformJwtEffect = createIssuePlatformJwtEffect({
 *     triggerAction: MakePaymentPaymentLinksApiActions.emailVerificationReceived,
 *     successAction: MakePaymentPaymentLinksApiActions.platformJwtObtained,
 *     errorAction:   MakePaymentPaymentLinksApiActions.platformJwtError,
 *   });
 */
export function createIssuePlatformJwtEffect<A extends Action<string>>(config: {
  triggerAction: ActionCreator<string, Creator<any[], A>>;
  successAction: ActionCreator;
  errorAction: ActionCreator;
}) {
  return createEffect(
    (
      actions$ = inject(Actions),
      store = inject(Store),
      paymentLinkService = inject(PaymentLinkService),
    ) =>
      actions$.pipe(
        ofType(config.triggerAction),
        concatLatestFrom(() => [
          store.select(selectTenantId),
          store.select(selectSessionId),
          store.select(selectCurrentLanguage),
        ]),
        exhaustMap(([, tenantId, sessionId, locale]) =>
          paymentLinkService
            .issuePlatformJwt(tenantId, { sessionId, locale })
            .pipe(
              map((response) => config.successAction(response) as Action),
              catchError((error: HttpErrorResponse) =>
                of(config.errorAction({ error }) as Action),
              ),
            ),
        ),
      ),
    { functional: true },
  );
}

/**
 * Factory that creates a reusable NgRx functional effect for re-sending the
 * sign-in verification email in the Payment Links flow.
 *
 * Unlike createVerifyUserEffect, this effect does not expect a username in the
 * triggering action. Instead it reads the previously stored username from the
 * btpay store via selectSignInUsername.
 */
export function createResendVerifyUserEffect<A extends Action<string>>(config: {
  triggerAction: ActionCreator<string, Creator<any[], A>>;
  successAction: ActionCreator;
  errorAction: ActionCreator;
}) {
  return createEffect(
    (
      actions$ = inject(Actions),
      store = inject(Store),
      paymentLinkService = inject(PaymentLinkService),
    ) =>
      actions$.pipe(
        ofType(config.triggerAction),
        concatLatestFrom(() => [
          store.select(selectTenantId),
          store.select(selectSessionId),

          store.select(selectSignInUsername),
        ]),
        exhaustMap(([, tenantId, sessionId, username]) =>
          paymentLinkService.verifyUser(tenantId, { username, sessionId }).pipe(
            map((response) => config.successAction(response) as Action),
            catchError((error: HttpErrorResponse) =>
              of(config.errorAction({ error }) as Action),
            ),
          ),
        ),
      ),
    { functional: true },
  );
}

/**
 * Factory that creates a reusable NgRx functional effect that persists the
 * platform JWT to localStorage via CoreService AND immediately seeds
 * AuthUserStore with the new access token by dispatching LoginSuccess.
 *
 * Dispatching LoginSuccess synchronously ensures TokenInterceptor.this.token
 * is updated (via its AuthUserStore subscription) before Angular CD fires any
 * component HTTP calls — eliminating the race where calls go out with the
 * stale anonymous JWT.
 *
 * createLoadUserEffect still fires on the same trigger and will dispatch a
 * second LoginSuccess with the full user profile once loadUserInfo completes,
 * safely overwriting the minimal payload dispatched here.
 *
 * Usage (POI, POA, international — each passes its own platformJwtObtained action):
 *
 *   export const poiPersistPlatformJwtEffect = createPersistPlatformJwtEffect({
 *     triggerAction: MakePaymentPaymentLinksApiActions.platformJwtObtained,
 *   });
 */
export function createPersistPlatformJwtEffect<
  A extends Action<string> & { accessToken: string },
>(config: { triggerAction: ActionCreator<string, Creator<any[], A>> }) {
  return createEffect(
    (
      actions$ = inject(Actions),
      coreService = inject(CoreService),
      store = inject(Store),
    ) =>
      actions$.pipe(
        ofType(config.triggerAction),
        concatLatestFrom(() => [store.select(ActiveTenantStore)]),
        exhaustMap(([action, activeTenant]) => {
          coreService.setLocalStorageForExternalLogin(
            action.accessToken,
            activeTenant,
            'arc-quantum',
            'arc-feature-paymentscore',
            'common-checkout-feature-persona',
          );
          return of(
            new LoginSuccess({
              user: { accessToken: action.accessToken } as User,
              activeTenant,
              activeRole: null,
              logoutUrl: null,
            }),
          );
        }),
      ),
    { functional: true },
  );
}

/**
 * Factory that creates a non-dispatching NgRx functional effect that
 * unsubscribes from email verification push notifications.
 *
 * The effect listens for the provided trigger action (typically continueAsGuestClicked)
 * and cancels the active email verification subscription via PushNotificationsService.
 *
 * Usage:
 *
 *   export const poiUnsubscribeFromEmailVerificationEffect =
 *     createUnsubscribeFromEmailVerificationEffect({
 *       triggerAction: PoiMakePaymentComponentActions.continueAsGuestClicked,
 *     });
 */
export function createUnsubscribeFromEmailVerificationEffect<
  A extends Action<string>,
>(config: { triggerAction: ActionCreator<string, Creator<any[], A>> }) {
  return createEffect(
    (
      actions$ = inject(Actions),
      pushNotificationsService = inject(PushNotificationsService),
    ) =>
      actions$.pipe(
        ofType(config.triggerAction),
        tap(() =>
          pushNotificationsService.unsubscribeFromEmailVerificationEvents(),
        ),
      ),
    { functional: true, dispatch: false },
  );
}

/**
 * Factory that creates a non-dispatching NgRx functional effect that switches
 * the checkout configuration from payment-links mode to regular platform mode
 * once the platform JWT has been obtained after email verification.
 *
 * Mirrors what `platformCheckoutResolver` sets at route activation time, so
 * that the in-flight checkout session reflects the authenticated user's
 * capabilities (e.g. save payment account, payment date selection) without
 * requiring a full route navigation.
 *
 * Usage (POI, POA, international — each passes its own platformJwtObtained action):
 *
 *   export const poiSwitchToRegularCheckoutEffect =
 *     createSwitchToRegularCheckoutEffect({
 *       triggerAction: MakePaymentPaymentLinksApiActions.platformJwtObtained,
 *     });
 */
export function createSwitchToRegularCheckoutEffect<
  A extends Action<string>,
>(config: { triggerAction: ActionCreator<string, Creator<any[], A>> }) {
  return createEffect(
    (actions$ = inject(Actions), appConfig = inject(AppConfigSettings)) =>
      actions$.pipe(
        ofType(config.triggerAction),
        tap(() => {
          appConfig.setconfig('usePaymentLinkEndpoints', 'false');
          appConfig.setconfig('disableSavePaymentAccount', 'false');
          appConfig.setconfig('disablePaymentDate', 'false');
        }),
      ),
    { functional: true, dispatch: false },
  );
}

/**
 * Factory that creates a non-dispatching NgRx functional effect that cancels
 * a pending email verification token when the buyer chooses to continue as guest.
 *
 * Only fires the HTTP call if `selectSignInEmailToken` is non-null — meaning a
 * verification email was actually sent successfully. If `verifyUser` returned an
 * error the token is null and no cancel call is made.
 *
 * Fire-and-forget: errors are swallowed so the user flow is never blocked.
 *
 * Usage (POI, POA, international — each passes its own continueAsGuestClicked action):
 *
 *   export const poiCancelVerificationEffect = createCancelVerificationEffect({
 *     triggerAction: PoiMakePaymentComponentActions.continueAsGuestClicked,
 *   });
 */
export function createCancelVerificationEffect<
  A extends Action<string>,
>(config: { triggerAction: ActionCreator<string, Creator<any[], A>> }) {
  return createEffect(
    (
      actions$ = inject(Actions),
      store = inject(Store),
      paymentLinkService = inject(PaymentLinkService),
    ) =>
      actions$.pipe(
        ofType(config.triggerAction),
        concatLatestFrom(() => [
          store.select(selectTenantId),
          store.select(selectSessionId),
          store.select(selectSignInEmailToken),
        ]),
        exhaustMap(([, tenantId, sessionId, signInEmailToken]) => {
          // Only cancel if a verification email was actually sent (null on verifyUser error)
          if (!signInEmailToken) return EMPTY;
          return paymentLinkService
            .cancelVerification(tenantId, sessionId)
            .pipe(catchError(() => EMPTY));
        }),
      ),
    { functional: true, dispatch: false },
  );
}

/**
 * Factory that creates a reusable NgRx functional effect that verifies the
 * user is provisioned in the backend after a platform JWT is obtained during
 * the Payment Links sign-in flow.
 *
 * The effect listens for a configurable trigger action carrying an accessToken
 * (typically platformJwtObtained), reads the current tenantId from the store,
 * calls the permission role-profile endpoint to confirm provisioning, and
 * dispatches either the provided success or error action.
 *
 * Usage (POI, POA, international — each passes its own action set):
 *
 *   export const poiVerifyUserIsProvisionedEffect =
 *     createVerifyUserIsProvisionedEffect({
 *       triggerAction: MakePaymentPaymentLinksApiActions.platformJwtObtained,
 *       successAction: MakePaymentPaymentLinksApiActions.verifyUserIsProvisionedSuccess,
 *       errorAction:   MakePaymentPaymentLinksApiActions.verifyUserIsProvisionedError,
 *     });
 */
export function createVerifyUserIsProvisionedEffect<
  A extends Action<string>,
>(config: {
  triggerAction: ActionCreator<string, Creator<any[], A>>;
  successAction: ActionCreator;
  errorAction: ActionCreator;
}) {
  return createEffect(
    (
      actions$ = inject(Actions),
      store = inject(Store),
      permissionService = inject(PermissionV1Service),
    ) =>
      actions$.pipe(
        ofType(config.triggerAction),
        concatLatestFrom(() => [
          store.select(selectTenantId),
          store.select(selectVerifyUserIsProvisionedRetryConfig),
          store.select(AuthUserStore),
        ]),
        exhaustMap(([, tenantId, retryConfig, authUserState]) =>
          permissionService
            .v1TenantsTenantIdApplicationApplicationIdFeatureFeatureIdPersonaPersonaIdRoleProfileGet(
              tenantId,
              'arc-quantum',
              'arc-feature-paymentscore',
              'common-checkout-feature-persona',
              authUserState?.user?.accessToken,
            )
            .pipe(
              retry(retryConfig),
              map(() => config.successAction() as Action),
              catchError((error: HttpErrorResponse) =>
                of(config.errorAction({ error }) as Action),
              ),
            ),
        ),
      ),
    { functional: true },
  );
}
